import React from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import VedicBirthForm from "@/components/VedicBirthForm";
import VedicHowItWorks from "@/components/VedicHowItWorks";
import { Flame, ScrollText, Sun, Users, ShieldAlert, ArrowRight, Sparkles } from "lucide-react";

const IMG = "https://images.unsplash.com/photo-1636227597176-c554bcbee419?crop=entropy&cs=srgb&fm=jpg&q=85";
const REMEDY_IMG = "https://images.pexels.com/photos/37531063/pexels-photo-37531063.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

const scrollToTop = () => window.scrollTo({ top: 0, behavior: "smooth" });

const DOSHAS = [
  { icon: Flame, name: "Mangal Dosha", desc: "Caused by Mars in certain houses. Can create friction in marriage and relationships if left unbalanced." },
  { icon: ShieldAlert, name: "Kaal Sarp Dosha", desc: "Formed when all planets fall between Rahu and Ketu. Brings obstacles, delays and sudden ups and downs." },
  { icon: Users, name: "Pitra Dosha", desc: "An ancestral debt in the chart. Addressed through shraddh, tarpan and offerings to honour forefathers." },
  { icon: Sun, name: "Shani Dosha", desc: "The influence of Saturn during Sade Sati or dhaiya. Tests patience, discipline and perseverance." },
];

const PUJAS = [
  { tag: "Mangal", name: "Mangal Shanti Puja", desc: "A powerful puja to calm the fire of Mars and restore harmony to relationships.", type: "worship", service: "srv-5", img: "https://images.unsplash.com/photo-1605302977545-3a09913be1dd?crop=entropy&cs=srgb&fm=jpg&q=85" },
  { tag: "Kaal Sarp", name: "Kaal Sarp Shanti", desc: "A sacred ritual to break the serpent's hold and clear the path of obstacles.", type: "ritual", service: "srv-11", img: "https://images.unsplash.com/photo-1662852744966-143f1dde2634?crop=entropy&cs=srgb&fm=jpg&q=85" },
  { tag: "Pitra", name: "Pitra Dosha Nivaran Puja", desc: "Honour your ancestors and clear the family debt for lasting peace and prosperity.", type: "worship", service: "srv-1", img: "https://images.unsplash.com/photo-1636227597176-c554bcbee419?crop=entropy&cs=srgb&fm=jpg&q=85" },
];

export function DoshaFeatures() {
  return (
    <>
      {/* Dosha types grid */}
      <section className="px-4 sm:px-6 lg:px-12 max-w-7xl mx-auto py-14 lg:py-20" data-testid="dosha-types-section">
        <div className="mb-10 max-w-2xl">
          <p className="text-xs uppercase tracking-[0.25em] font-semibold text-accent mb-3">Common Doshas</p>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-serif font-semibold tracking-tight">The afflictions we look for</h2>
          <p className="mt-3 text-muted-foreground leading-relaxed">Every chart tells a story. These are the doshas our purohits identify and gently correct.</p>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {DOSHAS.map((d) => (
            <div key={d.name} data-testid={`dosha-type-${d.name}`}>
              <span className="h-11 w-11 grid place-items-center rounded-xl bg-gradient-to-br from-accent/20 to-primary/20 text-primary mb-3"><d.icon className="h-5 w-5" /></span>
              <h3 className="font-serif text-lg font-semibold">{d.name}</h3>
              <p className="text-sm text-muted-foreground mt-1.5 leading-relaxed">{d.desc}</p>
              <button onClick={scrollToTop} data-testid={`dosha-knowmore-${d.name}`} className="inline-flex items-center gap-1.5 text-sm font-semibold text-primary mt-3 hover:gap-2.5 transition-all">Know more <ArrowRight className="h-4 w-4" /></button>
            </div>
          ))}
        </div>
      </section>

      {/* The old remedies for a troubled chart */}
      <section className="bg-secondary/40 border-y border-border" data-testid="dosha-remedies-section">
        <div className="px-4 sm:px-6 lg:px-12 max-w-7xl mx-auto py-14 lg:py-20 grid lg:grid-cols-2 gap-10 lg:gap-14 items-center">
          <div className="relative order-2 lg:order-1">
            <div className="absolute -inset-4 rounded-3xl bg-accent/10 blur-2xl" />
            <img src={REMEDY_IMG} alt="Sacred havan for dosha remedies" className="relative rounded-2xl w-full h-72 lg:h-[26rem] object-cover shadow-lg" />
          </div>
          <div className="order-1 lg:order-2">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-serif font-semibold tracking-tight leading-tight">The old remedies for a troubled chart</h2>
            <p className="mt-3 text-muted-foreground leading-relaxed max-w-lg">Each dosha has its own cure. The right mantra and ritual can turn a block into a road.</p>
            <div className="grid sm:grid-cols-2 gap-8 mt-8">
              <div>
                <p className="flex items-center gap-2 font-serif text-lg font-semibold"><ScrollText className="h-5 w-5 text-accent" /> Mantras</p>
                <p className="text-sm text-muted-foreground mt-2 leading-relaxed">Chant the Beej mantra for Mars to cool its heat and steady the mind.</p>
              </div>
              <div>
                <p className="flex items-center gap-2 font-serif text-lg font-semibold"><Flame className="h-5 w-5 text-accent" /> Rituals</p>
                <p className="text-sm text-muted-foreground mt-2 leading-relaxed">Offer water to the rising sun to honour your ancestors and clear old debts.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Booking — The right ritual for your Dosha */}
      <section className="px-4 sm:px-6 lg:px-12 max-w-7xl mx-auto py-14 lg:py-20" data-testid="dosha-pujas-section">
        <div className="mb-10 text-center max-w-2xl mx-auto">
          <p className="text-xs uppercase tracking-[0.25em] font-semibold text-accent mb-3">Pujas</p>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-serif font-semibold tracking-tight">The right ritual for your Dosha</h2>
          <p className="mt-3 text-muted-foreground leading-relaxed">Choose a puja and book it at a trusted Mandir with a verified purohit.</p>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {PUJAS.map((p) => (
            <div key={p.name} data-testid={`dosha-puja-${p.service}`} className="group rounded-2xl bg-card border border-border hover:border-accent overflow-hidden shadow-sm hover:shadow-lg hover-lift flex flex-col">
              <div className="relative h-40 overflow-hidden">
                <img src={p.img} alt={p.name} className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-110" />
                <span className="absolute top-3 left-3 text-[11px] font-medium px-2.5 py-1 rounded-full bg-background/90 text-foreground">Ritual</span>
              </div>
              <div className="p-5 flex flex-col flex-1">
                <h3 className="font-serif text-lg font-semibold leading-tight">{p.name}</h3>
                <p className="text-sm text-muted-foreground mt-2 line-clamp-2 flex-1">{p.desc}</p>
                <Link to={`/book/${p.type}?service=${p.service}`} data-testid={`dosha-puja-book-${p.service}`} className="inline-flex items-center gap-1.5 text-sm font-semibold text-primary mt-4 group-hover:gap-2.5 transition-all">Book <ArrowRight className="h-4 w-4" /></Link>
              </div>
            </div>
          ))}
        </div>
      </section>
    </>
  );
}

function Result(r) {
  return (
    <div className="rounded-2xl border border-border bg-card p-6 space-y-4">
      <p className="text-sm text-muted-foreground">{r.summary}</p>
      {r.doshas.map((d) => (
        <div key={d.name} className="rounded-xl border border-border p-4">
          <h4 className="font-serif font-semibold text-primary">{d.name}</h4>
          <p className="text-sm text-muted-foreground mt-1">{d.effect}</p>
          <p className="text-xs font-semibold uppercase tracking-wide text-accent mt-3 mb-1.5">Remedies</p>
          <ul className="space-y-1">{d.remedies.map((rem) => <li key={rem} className="flex gap-2 text-sm"><span className="text-accent">•</span> {rem}</li>)}</ul>
        </div>
      ))}
    </div>
  );
}

export default function Dosha() {
  return (
    <>
      <VedicBirthForm
        service="dosha" heroImg={IMG} sanskrit="॥ दोष निवारण ॥" title="Dosha & Traditional Solutions"
        intro="Identify Mangal, Pitra, Kaal Sarp and Shani doshas in your chart and receive traditional pujas, mantras and remedies."
        receive={["Identification of specific doshas", "Recommended pujas & rituals", "Mantra recommendations", "Astrological guidance for mitigation"]}
        endpoint="/vedic/dosha" renderResult={Result}
      />
      <DoshaFeatures />
      <VedicHowItWorks
        subtitle="Identify doshas in your chart and receive traditional remedies in four steps."
        steps={[
          { title: "Share birth details", desc: "Provide your name, date, time and place of birth." },
          { title: "Book online", desc: "Confirm your dosha analysis booking instantly." },
          { title: "Doshas identified", desc: "Mangal, Pitra, Kaal Sarp and Shani doshas are assessed." },
          { title: "Get remedies", desc: "Receive recommended pujas, mantras and remedies." },
        ]}
      />
    </>
  );
}
